import { OnInit, AfterViewInit, Component, Input, OnDestroy, ElementRef, afterNextRender} from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { fromEvent } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { CommonModule } from '@angular/common';
import { ComponentHeaderComponent } from '../component-header/component-header.component';
import { FontIconComponent } from '../font-icon/font-icon.component';
import { IntersectionDirective } from '@finder/shared/directives/intersection/intersection.directive';
import { PictureComponent } from '../picture/picture.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { AnchorComponent } from '../anchor/anchor.component';

const TRUE = 'true';
const FALSE = 'false';
interface RowData {
    rowIndex: string;
    columnIndex: string;
    isOpen: boolean;
};
@Component({
    selector: 'finder-shared-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: true,
    imports: [
        ButtonComponent,
        CommonModule,
        ComponentHeaderComponent,
        FontIconComponent,
        PictureComponent,
        SafeHtmlPipe,
        AnchorComponent,
    ]
})
export class SharedTableComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() data: any;

    isMobile = false;
    sync = false;
    version = 'v1';
    columnNavigation = false;

    private destroy$ = new Subject<void>();

    constructor(
        private viewportService: ViewportDetectionService,
        private elementReference: ElementRef,
    ) {
        afterNextRender(() => {
            this.setupSlidingColumns();
        });
    }

    ngOnInit() {
        this.isMobile = this.viewportService.isMobile();
        this.sync = this.data?.syncReadMore ?? false;
        this.columnNavigation = this.data?.columnNavigation ?? false;

        // Check for a new table version. A new v2 layout was added with "magic-key"
        this.version = this.data?.version ?? 'v1';
    }

    ngAfterViewInit() {
        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isMobile = this.viewportService.isMobile();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /* istanbul ignore next */
    getElementReference(): Element {
        return this.elementReference.nativeElement;
    }

    isReadMoreOpen(elementRef: HTMLElement): boolean {
        return this.getRowData(elementRef).isOpen;
    }

    checkForReadMore(elementRef: HTMLElement): string {
        const { rowIndex, columnIndex } = this.getRowData(elementRef);
        // check for read more first
        if (!this.data.rows[rowIndex]?.readMore) {
            return;
        }

        const content = this.data.rows[rowIndex].readMore[`column_${columnIndex}`];

        // Check for the data first. If you place it on the dataset it will be string undefined
        if (content) {
            // Set the content on the DOM and read it later
            elementRef.dataset.content = content;
            // Add the alignment class to the row
            elementRef.closest('tr').classList.add('read-more');
        }

        return content;
    }

    toggleReadMore(elementRef: HTMLElement) {
        const elements = this.sync ? elementRef.closest('tr').querySelectorAll('.read-more-container') : [elementRef];

        // Gather all the read more values and open them
        elements.forEach((elm: HTMLElement) => {
            elm.dataset.isOpen = this.isReadMoreOpen(elm) ? FALSE : TRUE;
        });
    }

    setupSlidingColumns() {
        if (this.version !== 'v2') {
            return;
        }

        /**
         * Listen for scroll events on the wrapper. When scrolling has ended we can do some
         * math to find the active column.
         * Get a reference to all our columns ahead of time so we don't have to look them up later.
         * We'll use the number of columns and wrapper width to find the distance the scroll should
         * snap to when a column is selected.
         * https://codepen.io/nolanlawson/pen/pGLvJp
         */
        const wrapper = this.getElementReference().querySelector('.scroll-wrapper');
        const wrapperOffset: number = wrapper.getBoundingClientRect().x;
        const ACTIVE_COLUMN = 'active-column';
        const columns = this.getElementReference().querySelectorAll(`.column-header`);
        const scrollDist = (wrapper.scrollWidth - wrapperOffset) / columns.length;

        // set the first column to active
        columns[1].classList.add(ACTIVE_COLUMN);

        fromEvent(wrapper, 'scroll')
            .pipe(
                takeUntil(this.destroy$),
                // wait until scrolling has stopped then figure out where we are
                debounceTime(100)
            )
            .subscribe((event: Event) => {
                // try and figure out the row you're in. watch for divide by 0
                const visibleRow = wrapper.scrollLeft ? Math.ceil((wrapper.scrollLeft - wrapperOffset) / scrollDist) + 1 : 1;

                // pick the active column from the list
                const activeColumn = columns[visibleRow];

                // clear the other classes
                columns.forEach((element: Element) => {
                    if (element.id === activeColumn.id) {
                        return;
                    }

                    element.classList.remove(ACTIVE_COLUMN);
                });

                // add the active class to the selected column
                activeColumn.classList.add(ACTIVE_COLUMN);
            });
    }

    selectColumn(columnNumber) {
        /**
         * we're using the previous icon because it can be hidden behind the floating column. we want
         * to make sure we force it into view.
         */
        this.getElementReference().querySelectorAll(`.column-${columnNumber} .previous`).forEach((element) => {
            element.scrollIntoView({
                behavior: 'smooth',
                // controls how the vertical axis is centered on the page
                block: 'nearest',
                // controls how the horizontal axis is centered on the page.
                inline: 'center'
            });
        });
    }

    extractHref(value: string): string {
        // first try to match double-quoted href
        let match = value.match(/href\s*=\s*"([^"]*)"/i);
        if (match) {
            return match[1];
        }

        // then try single-quoted href
        match = value.match(/href\s*=\s*'([^']*)'/i);
        if (match) {
            return match[1];
        }

        // finally try unquoted href
        match = value.match(/href\s*=\s*([^\s>]*)/i);

        return match ? match[1] : '#';
    }

    private getRowData(elementRef: HTMLElement): RowData {
        const [rowIndex, columnIndex] = (elementRef.dataset?.position ?? '').split('_');
        const isOpen = elementRef?.dataset?.isOpen === TRUE;

        return {
            rowIndex,
            columnIndex,
            isOpen,
        };
    }
}
